'use client';

import React from 'react';
import { Box } from '@mantine/core';
import { RootBox } from '@/shared/RootBox';
import { SectionTitle } from '@/shared/SectionTitle';
import { motion } from 'framer-motion';
import {
  BadgeCheck,
  ChartNoAxesCombined,
  Database,
  SearchCheck,
  BrainCircuit,
} from 'lucide-react';
import { SectionSubtitle } from '@/shared/SectionSubtitle';

type Card = {
  title: string;
  description: string;
  Icon: React.ComponentType<{ className?: string }>;
};

const cards: Card[] = [
  {
    title: 'AI Meets Human Precision',
    description:
      'Our approach combines the best of both worlds: an AI-powered engine backed by human expertise, control and precision. You can be confident that our results are always verified',
    Icon: BrainCircuit,
  },
  {
    title: 'Data-Driven, Objective Approach',
    description:
      'Our automated pre-selection process relies on revenue growth projections, so you can trust that decisions are based on hard data, not speculation or bias',
    Icon: Database,
  },
  {
    title: 'Refined Selection Process based on semi-automated Deep Research',
    description:
      'We narrow down the pool to the top few companies, filtering out underperformers and high-risk investments. This ensures you get venture companies with the highest investment potential',
    Icon: SearchCheck,
  },
  {
    title: 'Transparency and Trust',
    description:
      'Full visibility into the selection process, so you can see exactly how and why certain companies were chosen',
    Icon: BadgeCheck,
  },
];

const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

const CardItem = ({ card }: { card: Card }) => {
  const Icon = card.Icon;
  return (
    <div className="relative overflow-hidden rounded-3xl border border-black/5 bg-lightBlue-200/60 min-h-[290px] dark:border-white/10 dark:bg-secondary-500/15 p-10 shadow-[0_25px_70px_rgba(0,0,0,0.04)] backdrop-blur-sm  xxs:max-md:p-7">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-20 dark:opacity-10"
        style={{
          backgroundImage: "url('/bg/meshShapePrimaryReduced.svg')",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
      />
      <div className="pointer-events-none absolute left-8 top-8 inline-flex size-12 items-center justify-center rounded-full border border-primary-500/10 bg-primary-500/10 text-primary-500 dark:bg-primary-500/10 xxs:max-md:left-6 xxs:max-md:top-6">
        <Icon className="size-5" />
      </div>
      <div className="relative mt-14">
        <h3 className="font-montserrat text-2xl font-semibold leading-tight text-secondary-600 dark:text-lightBlue-200 xxs:max-md:text-xl">
          {card.title}
        </h3>
        <p className="mt-4 text-base leading-relaxed text-secondary-500 dark:text-lightBlue-200/90">
          {card.description}
        </p>
      </div>
    </div>
  );
};

export const BlockCards = () => {
  return (
    <Box className="relative z-0 w-full" id="thefutureofinvestment" style={{ overflow: 'hidden' }}>
      <RootBox>
        <SectionTitle title="The Future of Investment" index={5} />
          <SectionSubtitle subtitle="Where AI and Human Expertise Converge" />

        <motion.div
          className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2"
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          {cards.map((card) => (
            <motion.div key={card.title} variants={itemVariants}>
              <CardItem card={card} />
            </motion.div>
          ))}
        </motion.div>

        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => (window.location.href = 'https://calendly.com/arcanis/arcanis-intro')}
            className="rounded-lg bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-2 font-semibold text-gray-50 hover:from-primary-300 hover:to-primary-400 hover:text-gray-50"
          >
            Schedule a call
          </button>
        </div>
      </RootBox>
    </Box>
  );
};

BlockCards.displayName = 'BlockCards';

